import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getUserProfile } from '../services/api';
import { isSuperuser } from '../services/auth';

// グローバルで管理するユーザー情報（頻繁にアクセスされるもの）
interface UserInfo {
  user_id: number;
  username: string;
  display_name: string;
  birth_date: string | null;
  avatar: string | null;
  subscription_plan: number;
  is_superuser: boolean;
  personalize: {
    p: {
      lk: boolean;
    };
  };
}

interface UserContextType {
  user: UserInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // ユーザー情報を再取得（プロフィール更新時などに使用）
  refreshUser: () => Promise<void>;
  // ログアウト時にキャッシュをクリア
  clearUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      // プロフィールとスーパーユーザー情報を並列で取得
      const [profileResponse, isSuperuserResult] = await Promise.all([
        getUserProfile(),
        isSuperuser(),
      ]);
      const profile = profileResponse.data;

      // 必要な情報のみを抽出してキャッシュ
      setUser({
        user_id: profile.user_id,
        username: profile.username,
        display_name: profile.display_name,
        birth_date: profile.birth_date || null,
        avatar: profile.avatar,
        subscription_plan: profile.subscription_plan ?? 0,
        is_superuser: isSuperuserResult,
        personalize: profile.personalize ?? { p: { lk: false } },
      });
    } catch (error) {
      console.error('ユーザー情報の取得に失敗しました:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    await fetchUser();
  }, [fetchUser]);

  const clearUser = useCallback(() => {
    setUser(null);
  }, []);

  // 初回マウント時とトークン変更時にユーザー情報を取得
  useEffect(() => {
    fetchUser();

    // ログアウトイベントをリッスン
    const handleLogout = () => {
      clearUser();
    };

    // ログインイベントをリッスン（トークンが設定された時）
    const handleLoginSuccess = () => {
      fetchUser();
    };

    window.addEventListener('logout', handleLogout);
    window.addEventListener('loginSuccess', handleLoginSuccess);

    return () => {
      window.removeEventListener('logout', handleLogout);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
    };
  }, [fetchUser, clearUser]);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        refreshUser,
        clearUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// カスタムフック
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// 誕生日のみを取得するヘルパーフック（既存コードとの互換性のため）
export const useUserBirthDate = (): string | null => {
  const { user } = useUser();
  return user?.birth_date || null;
};
